import { FirebaseApp, getApps, initializeApp } from "firebase/app";
import {
  Analytics,
  getAnalytics,
  initializeAnalytics,
  isSupported,
  logEvent,
  setUserId,
} from "firebase/analytics";

type AnalyticsEventParams = Record<string, unknown>;

type FirebaseWebConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId: string;
};

const FIREBASE_ANALYTICS_APP_NAME = "gc-webnext-analytics";

let analyticsPromise: Promise<Analytics | null> | null = null;
let hasWarnedMissingConfig = false;
let hasWarnedUnsupported = false;
let hasWarnedInitFailure = false;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readStringValue(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function readConfigValue(
  sources: Array<Record<string, unknown> | undefined>,
  keys: string[],
): string | undefined {
  for (const source of sources) {
    if (!source) {
      continue;
    }

    for (const key of keys) {
      const value = readStringValue(source[key]);
      if (value) {
        return value;
      }
    }
  }

  return undefined;
}

function resolveRuntimeFirebaseConfig(): Record<string, unknown> | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const globalConfig = window.gc?.global?.config;
  if (!isRecord(globalConfig)) {
    return undefined;
  }

  const nestedCandidates = [
    globalConfig.firebase_web_config,
    globalConfig.firebase_config,
  ];

  for (const candidate of nestedCandidates) {
    if (isRecord(candidate)) {
      return candidate;
    }
  }

  return undefined;
}

function resolveFirebaseWebConfig(): FirebaseWebConfig | null {
  if (typeof window === "undefined") {
    return null;
  }

  const runtimeRootConfig = isRecord(window.gc?.global?.config)
    ? (window.gc.global.config as Record<string, unknown>)
    : undefined;
  const runtimeNestedConfig = resolveRuntimeFirebaseConfig();
  const envConfig = import.meta.env as unknown as Record<string, string | undefined>;
  const envSource: Record<string, unknown> = {
    apiKey: envConfig.VITE_FIREBASE_API_KEY,
    authDomain: envConfig.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: envConfig.VITE_FIREBASE_PROJECT_ID,
    storageBucket: envConfig.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: envConfig.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: envConfig.VITE_FIREBASE_APP_ID,
    measurementId: envConfig.VITE_FIREBASE_MEASUREMENT_ID,
  };

  const sources = [runtimeNestedConfig, runtimeRootConfig, envSource];

  const config: FirebaseWebConfig = {
    apiKey: readConfigValue(sources, ["apiKey", "api_key", "firebase_api_key"]) ?? "",
    authDomain:
      readConfigValue(sources, ["authDomain", "auth_domain", "firebase_auth_domain"]) ?? "",
    projectId:
      readConfigValue(sources, ["projectId", "project_id", "firebase_project_id"]) ?? "",
    storageBucket:
      readConfigValue(sources, ["storageBucket", "storage_bucket", "firebase_storage_bucket"]) ??
      "",
    messagingSenderId:
      readConfigValue(sources, [
        "messagingSenderId",
        "messaging_sender_id",
        "firebase_messaging_sender_id",
      ]) ?? "",
    appId: readConfigValue(sources, ["appId", "app_id", "firebase_app_id"]) ?? "",
    measurementId:
      readConfigValue(sources, ["measurementId", "measurement_id", "firebase_measurement_id"]) ??
      "",
  };

  const missingKeys = Object.entries(config)
    .filter(([, value]) => !value)
    .map(([key]) => key);

  if (missingKeys.length > 0) {
    if (!hasWarnedMissingConfig && !import.meta.env.PROD) {
      console.info(
        "[analytics] Firebase analytics config missing keys:",
        missingKeys.join(", "),
      );
      hasWarnedMissingConfig = true;
    }
    return null;
  }

  return config;
}

function getOrCreateFirebaseApp(firebaseConfig: FirebaseWebConfig): FirebaseApp {
  const existing = getApps().find((app) => app.name === FIREBASE_ANALYTICS_APP_NAME);
  if (existing) {
    return existing;
  }

  return initializeApp(firebaseConfig, FIREBASE_ANALYTICS_APP_NAME);
}

function isAnalyticsAlreadyInitializedError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    error.code === "analytics/already-initialized"
  );
}

async function getFirebaseAnalytics(): Promise<Analytics | null> {
  if (analyticsPromise) {
    return analyticsPromise;
  }

  analyticsPromise = (async () => {
    if (typeof window === "undefined") {
      return null;
    }

    const firebaseConfig = resolveFirebaseWebConfig();
    if (!firebaseConfig) {
      return null;
    }

    const supported = await isSupported().catch(() => false);
    if (!supported) {
      if (!hasWarnedUnsupported && !import.meta.env.PROD) {
        console.info("[analytics] Firebase analytics is not supported in this browser/context.");
        hasWarnedUnsupported = true;
      }
      return null;
    }

    try {
      const app = getOrCreateFirebaseApp(firebaseConfig);
      return initializeAnalytics(app, {
        config: { send_page_view: false },
      });
    } catch (error) {
      if (isAnalyticsAlreadyInitializedError(error)) {
        const app = getOrCreateFirebaseApp(firebaseConfig);
        return getAnalytics(app);
      }

      if (!hasWarnedInitFailure && !import.meta.env.PROD) {
        console.warn("[analytics] Firebase analytics init failed.", error);
        hasWarnedInitFailure = true;
      }
      return null;
    }
  })();

  return analyticsPromise;
}

export async function logFirebaseAnalyticsEvent(
  eventName: string,
  params: AnalyticsEventParams,
): Promise<boolean> {
  const analytics = await getFirebaseAnalytics();
  if (!analytics) {
    return false;
  }

  try {
    logEvent(analytics, eventName, params);
    return true;
  } catch (error) {
    if (!import.meta.env.PROD) {
      console.warn("[analytics] Failed to log Firebase event.", eventName, error);
    }
    return false;
  }
}

export async function setFirebaseAnalyticsUserId(
  userId: string | null,
): Promise<boolean> {
  const analytics = await getFirebaseAnalytics();
  if (!analytics) {
    return false;
  }

  try {
    setUserId(analytics, userId);
    return true;
  } catch (error) {
    if (!import.meta.env.PROD) {
      console.warn("[analytics] Failed to set Firebase user id.", error);
    }
    return false;
  }
}
